import * as XLSX from 'xlsx';
import { api } from './api';

export class ExcelExporter {
  constructor() {
    this.workbook = null;
  }

  // Export Budget Dashboard data to Excel
  async exportBudgetDashboard(filters = {}, options = {}) {
    try {
      // Prepare query parameters
      const params = new URLSearchParams();
      
      if (filters.dateRange?.start) {
        params.append('start', filters.dateRange.start.toISOString().split('T')[0]);
      }
      if (filters.dateRange?.end) {
        params.append('end', filters.dateRange.end.toISOString().split('T')[0]);
      }
      if (filters.departments?.length > 0) {
        params.append('departments', filters.departments.join(','));
      }
      if (filters.categories?.length > 0) {
        params.append('categories', filters.categories.join(','));
      }
      if (filters.expenseTypes?.length > 0) {
        params.append('expenseTypes', filters.expenseTypes.join(','));
      }
      if (filters.expenseGroups?.length > 0) {
        params.append('expenseGroups', filters.expenseGroups.join(','));
      }

      // Make API call to get Excel file
      const response = await fetch(`${api.defaults.baseURL}/export/budget-dashboard?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // Add auth headers if needed
        }
      });

      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      // Get the blob and create download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 10);
      link.download = `budget-dashboard-${timestamp}.xlsx`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, message: 'Excel file downloaded successfully' };
    } catch (error) {
      console.error('Excel export error:', error);
      return { success: false, error: error.message };
    }
  }

  // Alternative client-side Excel export (if backend is not available)
  async exportDashboardDataClientSide(dashboardData, filters = {}) {
    try {
      // Create a new workbook
      const workbook = XLSX.utils.book_new();

      // 1. Summary Sheet
      const summaryData = [
        ['Budget Dashboard Summary'],
        ['Generated on:', new Date().toLocaleDateString()],
        ['Date Range:', this.formatDateRange(filters.dateRange)],
        [],
        ['Key Metrics'],
        ['Metric', 'Value'],
        ['Total Budget', dashboardData.metrics?.totalBudget || 0],
        ['Total Spent', dashboardData.metrics?.totalSpent || 0],
        ['Remaining Budget', dashboardData.metrics?.remainingBudget || 0],
        ['Budget Utilization', `${dashboardData.metrics?.utilizationRate || 0}%`]
      ];

      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Dashboard Summary');

      // 2. Department Breakdown Sheet
      if (dashboardData.departmentBreakdown?.length > 0) {
        const deptData = [
          ['Department Breakdown'],
          [],
          ['Department', 'Budget', 'Spent', 'Remaining', 'Utilization %']
        ];

        dashboardData.departmentBreakdown.forEach(dept => {
          deptData.push([
            dept.department || 'Unknown',
            dept.budget || 0,
            dept.spent || 0,
            dept.remaining || 0,
            `${dept.utilization || 0}%`
          ]);
        });

        const deptSheet = XLSX.utils.aoa_to_sheet(deptData);
        XLSX.utils.book_append_sheet(workbook, deptSheet, 'Department Breakdown');
      }

      // 3. Monthly Trends Sheet
      if (dashboardData.monthlyTrends?.length > 0) {
        const trendsData = [
          ['Monthly Spending Trends'],
          [],
          ['Month', 'Ordered Qty', 'Received Qty', 'Ordered Amount', 'Received Amount']
        ];

        dashboardData.monthlyTrends.forEach(trend => {
          trendsData.push([
            trend.month || '',
            trend.orderedQty || 0,
            trend.receivedQty || 0,
            trend.orderedAmount || 0,
            trend.receivedAmount || 0
          ]);
        });

        const trendsSheet = XLSX.utils.aoa_to_sheet(trendsData);
        XLSX.utils.book_append_sheet(workbook, trendsSheet, 'Monthly Trends');
      }

      // 4. Category Breakdown Sheet
      if (dashboardData.topCategories?.length > 0) {
        const categoryData = [
          ['Category Breakdown'],
          [],
          ['Category', 'Amount Spent', 'Percentage']
        ];

        dashboardData.topCategories.forEach(category => {
          categoryData.push([
            category.category || 'Unknown',
            category.amount || 0,
            `${category.percentage || 0}%`
          ]);
        });

        const categorySheet = XLSX.utils.aoa_to_sheet(categoryData);
        XLSX.utils.book_append_sheet(workbook, categorySheet, 'Category Breakdown');
      }

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `budget-dashboard-${timestamp}.xlsx`;

      // Write and download the file
      XLSX.writeFile(workbook, filename);

      return { success: true, message: 'Excel file downloaded successfully' };
    } catch (error) {
      console.error('Client-side Excel export error:', error);
      return { success: false, error: error.message };
    }
  }

  // Helper method to format date range
  formatDateRange(dateRange) {
    if (!dateRange || (!dateRange.start && !dateRange.end)) {
      return 'All time';
    }
    
    const start = dateRange.start ? dateRange.start.toLocaleDateString() : 'Beginning';
    const end = dateRange.end ? dateRange.end.toLocaleDateString() : 'Now';
    
    return `${start} - ${end}`;
  }

  // Export specific chart data
  exportChartData(chartData, chartTitle, format = 'xlsx') {
    try {
      const workbook = XLSX.utils.book_new();
      
      // Convert chart data to worksheet format
      let worksheetData = [];
      
      if (chartData.labels && chartData.datasets) {
        // Chart.js format
        const headers = ['Label', ...chartData.datasets.map(ds => ds.label || 'Data')];
        worksheetData.push(headers);
        
        chartData.labels.forEach((label, index) => {
          const row = [label];
          chartData.datasets.forEach(dataset => {
            row.push(dataset.data[index] || 0);
          });
          worksheetData.push(row);
        });
      } else if (Array.isArray(chartData)) {
        // Array format
        if (chartData.length > 0) {
          const headers = Object.keys(chartData[0]);
          worksheetData.push(headers);
          
          chartData.forEach(item => {
            const row = headers.map(header => item[header] || '');
            worksheetData.push(row);
          });
        }
      }
      
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, chartTitle || 'Chart Data');
      
      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `${(chartTitle || 'chart').replace(/\s+/g, '_')}_${timestamp}.${format}`;
      
      XLSX.writeFile(workbook, filename);
      
      return { success: true, message: `${format.toUpperCase()} file downloaded successfully` };
    } catch (error) {
      console.error('Chart export error:', error);
      return { success: false, error: error.message };
    }
  }
}

// Create a singleton instance
export const excelExporter = new ExcelExporter();

// Export utility functions
export const exportBudgetDashboard = (filters, options) => {
  return excelExporter.exportBudgetDashboard(filters, options);
};

export const exportDashboardDataClientSide = (dashboardData, filters) => {
  return excelExporter.exportDashboardDataClientSide(dashboardData, filters);
};

export const exportChartData = (chartData, chartTitle, format) => {
  return excelExporter.exportChartData(chartData, chartTitle, format);
};